/**
 * Finance · Cash Flow 360 Workspace (Wave 3).
 *
 * Questions this workspace answers:
 *   "Where did the money come from? Where did it go? What will happen next?"
 *
 * Contract-driven per Objects 022 (Cash Flow Period) · 021 (Invoice) ·
 * 023 (Expense) · 025 (Vendor Payment).
 *
 * Reuses existing primitives only. Each month bar is a drill target
 * exposing every category of inflow/outflow that composes the number.
 */
import { useMemo, useState } from "react";
import { Waves } from "lucide-react";
import { Card, SectionHeader, DeltaChip } from "../../components/lib";
import { Drawer, Field, Segmented, ListRow } from "../../components/lib.overlay";
import { AuditTrail } from "../../components/AuditTrail";
import {
  CASH_FLOW, CASH_FORECAST_START_INDEX, INVOICES,
  EXPENSES, VENDOR_PAYMENTS, CashPoint,
} from "../../mocks/financeData";

type CashTab =
  | "overview" | "receipts" | "payments" | "payroll"
  | "marketing" | "operations" | "software" | "taxes"
  | "ai" | "forecast" | "audit";

/** For a given month bucket, derive category breakdown from live data.
 *  We treat the last non-forecast month as "current" and older buckets as historical.
 *  Since the mock data doesn't tag rows by month, we distribute by category weights.
 */
function categoryWeights() {
  const weightsIn = { receipts: 1 };
  const weightsOut = { payroll: 0, marketing: 0, operations: 0, software: 0, taxes: 0, other: 0 };
  EXPENSES.forEach((e) => {
    if (e.category === "salaries") weightsOut.payroll += e.amountEGP;
    else if (e.category === "marketing") weightsOut.marketing += e.amountEGP;
    else if (e.category === "logistics" || e.category === "cogs") weightsOut.operations += e.amountEGP;
    else if (e.category === "software") weightsOut.software += e.amountEGP;
    else if (e.category === "rent") weightsOut.other += e.amountEGP;
    else weightsOut.other += e.amountEGP;
  });
  // Add vendor payments to operations bucket
  VENDOR_PAYMENTS.forEach((v) => { weightsOut.operations += v.amountEGP; });
  // Rough tax = 14% of receipts (VAT proxy)
  const receiptsTotal = INVOICES.filter((i) => i.status === "paid" || i.status === "partial").reduce((n, i) => n + i.paidEGP, 0);
  weightsOut.taxes = receiptsTotal * 0.14;
  const totalOut = Object.values(weightsOut).reduce((n, v) => n + v, 0) || 1;
  return { weightsIn, weightsOut, totalOut };
}

export default function CashFlow() {
  const [monthIdx, setMonthIdx] = useState<number | null>(null);
  const [wsTab, setWsTab] = useState<CashTab>("overview");

  const maxAbs = Math.max(1, ...CASH_FLOW.flatMap((c) => [c.inflow, c.outflow]));

  const totalIn  = CASH_FLOW.filter((_, i) => i < CASH_FORECAST_START_INDEX).reduce((n, c) => n + c.inflow, 0);
  const totalOut = CASH_FLOW.filter((_, i) => i < CASH_FORECAST_START_INDEX).reduce((n, c) => n + c.outflow, 0);
  const currentBalance = CASH_FLOW[CASH_FORECAST_START_INDEX - 1]?.balance ?? 0;
  const forecastBalance = CASH_FLOW[CASH_FLOW.length - 1]?.balance ?? currentBalance;
  const runwayNet = totalIn - totalOut;

  const weights = useMemo(() => categoryWeights(), []);

  const monthData = useMemo(() => {
    if (monthIdx === null) return null;
    const m = CASH_FLOW[monthIdx];
    if (!m) return null;
    const isForecast = monthIdx >= CASH_FORECAST_START_INDEX;
    // Split outflow by weights
    const outByCat = {
      payroll:    m.outflow * (weights.weightsOut.payroll    / (weights.totalOut || 1)),
      marketing:  m.outflow * (weights.weightsOut.marketing  / (weights.totalOut || 1)),
      operations: m.outflow * (weights.weightsOut.operations / (weights.totalOut || 1)),
      software:   m.outflow * (weights.weightsOut.software   / (weights.totalOut || 1)),
      taxes:      m.outflow * (weights.weightsOut.taxes      / (weights.totalOut || 1)),
      other:      m.outflow * (weights.weightsOut.other      / (weights.totalOut || 1)),
    };
    return { m, isForecast, outByCat };
  }, [monthIdx, weights]);

  const receipts = useMemo(
    () => INVOICES.filter((i) => i.status === "paid" || i.status === "partial").sort((a, b) => b.paidEGP - a.paidEGP),
    [],
  );
  const payments = useMemo(
    () => [...VENDOR_PAYMENTS].sort((a, b) => b.amountEGP - a.amountEGP),
    [],
  );

  return (
    <div className="space-y-6" data-testid="fn-cash">
      {/* ── Snapshot ─────────────────────────────────────────────── */}
      <Card testId="fn-cash-summary">
        <SectionHeader eyebrow="Cash Flow · Auto-generated · Unaudited · Management projection" title="Cash flow snapshot" action={<Waves size={14} style={{ color: "var(--ph-ink-muted)" }} />} />
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--ph-ink-muted)" }}>Cash in</div>
            <div className="text-xl font-semibold tabular-nums mt-1" style={{ color: "var(--ph-success)" }}>EGP {totalIn}K</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--ph-ink-muted)" }}>Cash out</div>
            <div className="text-xl font-semibold tabular-nums mt-1" style={{ color: "var(--ph-danger)" }}>EGP {totalOut}K</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--ph-ink-muted)" }}>Net (actuals)</div>
            <div className="text-xl font-semibold tabular-nums mt-1" style={{ color: runwayNet >= 0 ? "var(--ph-success)" : "var(--ph-danger)" }}>{runwayNet >= 0 ? "+" : ""}EGP {runwayNet}K</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--ph-ink-muted)" }}>Current balance</div>
            <div className="text-xl font-semibold tabular-nums mt-1" style={{ color: "var(--ph-ink-strong)" }}>EGP {currentBalance}K</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--ph-ink-muted)" }}>Forecast (end)</div>
            <div className="text-xl font-semibold tabular-nums mt-1" style={{ color: forecastBalance >= currentBalance ? "var(--ph-success)" : "var(--ph-warning)" }}>EGP {forecastBalance}K</div>
          </div>
        </div>
      </Card>

      {/* ── Bars ─────────────────────────────────────────────────── */}
      <Card testId="fn-cash-in-out">
        <SectionHeader eyebrow="Click a month to drill" title="Inflows vs outflows" />
        <div className="grid gap-3" style={{ gridTemplateColumns: `160px repeat(${CASH_FLOW.length}, 1fr)` }}>
          <div className="text-[10px] uppercase tracking-[0.14em] pt-2" style={{ color: "var(--ph-success)" }}>Inflow</div>
          {CASH_FLOW.map((c, i) => (
            <button
              key={`in-${c.month}`}
              data-testid={`fn-cash-bar-in-${c.month}`}
              onClick={() => { setMonthIdx(i); setWsTab("overview"); }}
              className="flex flex-col items-center gap-1"
              style={{ height: 90, background: "transparent", border: 0 }}
            >
              <div className="flex-1 w-full flex items-end">
                <div style={{
                  width: "100%",
                  height: `${(c.inflow / maxAbs) * 100}%`,
                  background: "var(--ph-success)",
                  opacity: i >= CASH_FORECAST_START_INDEX ? 0.55 : 1,
                  borderRadius: "var(--ph-radius-sm) var(--ph-radius-sm) 0 0",
                }} />
              </div>
              <div className="text-[10px] tabular-nums" style={{ color: "var(--ph-ink-muted)" }}>{c.inflow}K</div>
            </button>
          ))}

          <div className="text-[10px] uppercase tracking-[0.14em] pt-2" style={{ color: "var(--ph-danger)" }}>Outflow</div>
          {CASH_FLOW.map((c, i) => (
            <button
              key={`out-${c.month}`}
              data-testid={`fn-cash-bar-out-${c.month}`}
              onClick={() => { setMonthIdx(i); setWsTab("overview"); }}
              className="flex flex-col items-center gap-1"
              style={{ height: 90, background: "transparent", border: 0 }}
            >
              <div className="flex-1 w-full flex items-end">
                <div style={{
                  width: "100%",
                  height: `${(c.outflow / maxAbs) * 100}%`,
                  background: "var(--ph-danger)",
                  opacity: i >= CASH_FORECAST_START_INDEX ? 0.55 : 1,
                  borderRadius: "var(--ph-radius-sm) var(--ph-radius-sm) 0 0",
                }} />
              </div>
              <div className="text-[10px] tabular-nums" style={{ color: "var(--ph-ink-muted)" }}>{c.outflow}K</div>
            </button>
          ))}

          <div />
          {CASH_FLOW.map((c, i) => (
            <div key={`lbl-${c.month}`} className="text-center text-[10px] uppercase tracking-[0.14em]"
              style={{ color: i >= CASH_FORECAST_START_INDEX ? "var(--ph-brand-accent)" : "var(--ph-ink-muted)" }}>
              {c.month}{i >= CASH_FORECAST_START_INDEX ? "*" : ""}
            </div>
          ))}
        </div>
        <div className="mt-3 text-[10px]" style={{ color: "var(--ph-ink-muted)" }}>* forecast · click any bar to drill into that month</div>
      </Card>

      {/* ── Monthly table ────────────────────────────────────────── */}
      <Card padded={false} testId="fn-cash-table">
        <div className="p-5" style={{ borderBottom: "1px solid var(--ph-border-subtle)" }}>
          <SectionHeader eyebrow="Monthly · click a row to drill" title="Cash movement" />
        </div>
        <div className="p-5">
          <div className="hidden md:grid text-[10px] uppercase tracking-[0.14em] pb-2" style={{ color: "var(--ph-ink-muted)", gridTemplateColumns: "1fr 1fr 1fr 1fr 1fr" }}>
            <div>Month</div>
            <div className="text-right">Inflow</div>
            <div className="text-right">Outflow</div>
            <div className="text-right">Net</div>
            <div className="text-right">Balance</div>
          </div>
          {CASH_FLOW.map((c: CashPoint, i) => {
            const net = c.inflow - c.outflow;
            const isForecast = i >= CASH_FORECAST_START_INDEX;
            return (
              <button
                key={c.month}
                data-testid={`fn-cash-row-${c.month}`}
                onClick={() => { setMonthIdx(i); setWsTab("overview"); }}
                className="grid items-center py-2 w-full text-left transition-colors"
                style={{ gridTemplateColumns: "1fr 1fr 1fr 1fr 1fr", borderTop: "1px solid var(--ph-border-subtle)" }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "var(--ph-hover-bg)")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
              >
                <div className="text-sm font-semibold" style={{ color: isForecast ? "var(--ph-brand-accent)" : "var(--ph-ink-strong)" }}>{c.month}{isForecast ? " · forecast" : ""}</div>
                <div className="text-sm tabular-nums text-right" style={{ color: "var(--ph-success)" }}>EGP {c.inflow}K</div>
                <div className="text-sm tabular-nums text-right" style={{ color: "var(--ph-danger)" }}>EGP {c.outflow}K</div>
                <div className="text-sm tabular-nums text-right font-semibold" style={{ color: net >= 0 ? "var(--ph-success)" : "var(--ph-danger)" }}>{net >= 0 ? "+" : ""}EGP {net}K</div>
                <div className="text-sm tabular-nums text-right" style={{ color: "var(--ph-ink-strong)" }}>EGP {c.balance}K</div>
              </button>
            );
          })}
        </div>
      </Card>

      {/* ── Month drawer ─────────────────────────────────────────── */}
      <Drawer open={monthIdx !== null} onClose={() => setMonthIdx(null)} title="Cash Month 360" testId="fn-cash-drawer" width={720}>
        {monthData && (
          <div>
            <div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: monthData.isForecast ? "var(--ph-brand-accent)" : "var(--ph-brand-primary)" }}>
              {monthData.isForecast ? "Forecast" : "Actual"}
            </div>
            <div style={{ fontFamily: "var(--ph-display-font)", fontSize: "1.35rem", color: "var(--ph-ink-strong)" }} className="mt-1 mb-3">{monthData.m.month}</div>

            <div className="grid grid-cols-4 gap-3 mb-4">
              <div><div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--ph-ink-muted)" }}>Inflow</div><div className="text-sm font-semibold tabular-nums" style={{ color: "var(--ph-success)" }}>EGP {monthData.m.inflow}K</div></div>
              <div><div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--ph-ink-muted)" }}>Outflow</div><div className="text-sm font-semibold tabular-nums" style={{ color: "var(--ph-danger)" }}>EGP {monthData.m.outflow}K</div></div>
              <div><div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--ph-ink-muted)" }}>Net</div><div className="text-sm font-semibold tabular-nums" style={{ color: (monthData.m.inflow - monthData.m.outflow) >= 0 ? "var(--ph-success)" : "var(--ph-danger)" }}>{monthData.m.inflow - monthData.m.outflow >= 0 ? "+" : ""}EGP {monthData.m.inflow - monthData.m.outflow}K</div></div>
              <div><div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--ph-ink-muted)" }}>Balance</div><div className="text-sm font-semibold tabular-nums">EGP {monthData.m.balance}K</div></div>
            </div>

            <div className="mb-4 overflow-x-auto">
              <Segmented<CashTab>
                testId="fn-cash-ws-tabs"
                value={wsTab}
                onChange={setWsTab}
                options={[
                  { value: "overview",   label: "Overview"   },
                  { value: "receipts",   label: "Receipts"   },
                  { value: "payments",   label: "Payments"   },
                  { value: "payroll",    label: "Payroll"    },
                  { value: "marketing",  label: "Marketing"  },
                  { value: "operations", label: "Operations" },
                  { value: "software",   label: "Software"   },
                  { value: "taxes",      label: "Taxes"      },
                  { value: "ai",         label: "AI note"    },
                  { value: "forecast",   label: "Forecast"   },
                  { value: "audit",      label: "Audit"      },
                ]}
              />
            </div>

            {wsTab === "overview" && (
              <div data-testid="fn-cash-ws-overview">
                <Field label="Type" inline><span className="text-sm">{monthData.isForecast ? "Forecast" : "Actual"}</span></Field>
                <div className="mt-3 text-[10px] uppercase tracking-[0.14em] mb-1.5" style={{ color: "var(--ph-ink-muted)" }}>Outflow composition</div>
                {Object.entries(monthData.outByCat).map(([k, v]) => (
                  <div key={k} className="grid items-center py-1" style={{ gridTemplateColumns: "1.4fr 3fr 1fr" }}>
                    <div className="text-sm capitalize" style={{ color: "var(--ph-ink-strong)" }}>{k}</div>
                    <div className="h-2 rounded" style={{ background: "var(--ph-border-subtle)", overflow: "hidden" }}>
                      <div style={{ width: `${Math.max(2, (v / monthData.m.outflow) * 100)}%`, height: "100%", background: "var(--ph-danger)" }} />
                    </div>
                    <div className="text-sm tabular-nums text-right" style={{ color: "var(--ph-danger)" }}>EGP {v.toFixed(0)}K</div>
                  </div>
                ))}
              </div>
            )}

            {wsTab === "receipts" && (
              <div data-testid="fn-cash-ws-receipts">
                <div className="text-[10px] uppercase tracking-[0.14em] mb-2" style={{ color: "var(--ph-ink-muted)" }}>Customer receipts · {receipts.length}</div>
                {receipts.slice(0, 25).map((i) => (
                  <ListRow key={i.id} testId={`fn-cash-receipt-${i.id}`}
                    label={<>{i.customer} <span className="text-[10px] font-mono" style={{ color: "var(--ph-ink-muted)" }}>{i.number}</span></>}
                    sublabel={<>{i.status} · {i.dueRelative}</>}
                    trailing={<span className="text-sm tabular-nums font-semibold" style={{ color: "var(--ph-success)" }}>EGP {i.paidEGP.toLocaleString()}</span>}
                  />
                ))}
              </div>
            )}

            {wsTab === "payments" && (
              <div data-testid="fn-cash-ws-payments">
                <div className="text-[10px] uppercase tracking-[0.14em] mb-2" style={{ color: "var(--ph-ink-muted)" }}>Vendor payments · {payments.length}</div>
                {payments.slice(0, 25).map((v) => (
                  <ListRow key={v.id} testId={`fn-cash-payment-${v.id}`}
                    label={<>{v.vendor} <span className="text-[10px] font-mono" style={{ color: "var(--ph-ink-muted)" }}>{v.reference}</span></>}
                    sublabel={<>{v.method} · {v.status} · {v.dueRelative}</>}
                    trailing={<span className="text-sm tabular-nums font-semibold" style={{ color: "var(--ph-danger)" }}>EGP {v.amountEGP.toLocaleString()}</span>}
                  />
                ))}
              </div>
            )}

            {(["payroll", "marketing", "operations", "software"] as const).map((cat) => wsTab === cat && (
              <div key={cat} data-testid={`fn-cash-ws-${cat}`}>
                <Field label="Estimated month spend" inline><span className="text-sm tabular-nums font-semibold" style={{ color: "var(--ph-danger)" }}>EGP {monthData.outByCat[cat].toFixed(0)}K</span></Field>
                <Field label="Share of outflow"     inline><span className="text-sm tabular-nums">{((monthData.outByCat[cat] / monthData.m.outflow) * 100).toFixed(1)}%</span></Field>
                <div className="mt-3 text-[10px] uppercase tracking-[0.14em] mb-1.5" style={{ color: "var(--ph-ink-muted)" }}>Contributing expenses</div>
                {EXPENSES
                  .filter((e) =>
                    (cat === "payroll" && e.category === "salaries") ||
                    (cat === "marketing" && e.category === "marketing") ||
                    (cat === "operations" && (e.category === "cogs" || e.category === "logistics")) ||
                    (cat === "software" && e.category === "software"))
                  .slice(0, 12)
                  .map((e) => (
                    <ListRow key={e.id} testId={`fn-cash-exp-${cat}-${e.id}`}
                      label={e.vendor}
                      sublabel={<>{e.description} · {e.status}</>}
                      trailing={<span className="text-sm tabular-nums">EGP {e.amountEGP.toLocaleString()}</span>}
                    />
                  ))}
              </div>
            ))}

            {wsTab === "taxes" && (
              <div data-testid="fn-cash-ws-taxes">
                <Field label="VAT collected (est.)" inline><span className="text-sm tabular-nums" style={{ color: "var(--ph-warning)" }}>EGP {monthData.outByCat.taxes.toFixed(0)}K</span></Field>
                <Field label="Base rate"            inline><span className="text-sm tabular-nums">14%</span></Field>
                <div className="text-sm py-2" style={{ color: "var(--ph-ink-muted)" }}>Detailed tax filings live in Documents · Tax filings.</div>
              </div>
            )}

            {wsTab === "ai" && (
              <div data-testid="fn-cash-ws-ai">
                <div className="text-sm p-3" style={{ background: "var(--ph-surface)", borderRadius: "var(--ph-radius-md)", color: "var(--ph-ink-default)" }}>
                  {monthData.isForecast
                    ? `Forecast month · Model expects EGP ${monthData.m.inflow - monthData.m.outflow}K net. Confidence tapers with distance from today; treat months 2-3 out as directional, not committed.`
                    : `Actual month · Net ${monthData.m.inflow - monthData.m.outflow >= 0 ? "positive" : "negative"}. Concentration risk: taxes + operations represent ${(((monthData.outByCat.operations + monthData.outByCat.taxes) / monthData.m.outflow) * 100).toFixed(0)}% of outflow.`}
                </div>
              </div>
            )}

            {wsTab === "forecast" && (
              <div data-testid="fn-cash-ws-forecast">
                <div className="text-[10px] uppercase tracking-[0.14em] mb-2" style={{ color: "var(--ph-ink-muted)" }}>Next 3 months</div>
                {CASH_FLOW.slice(CASH_FORECAST_START_INDEX, CASH_FORECAST_START_INDEX + 3).map((c) => (
                  <div key={c.month} className="grid items-center py-1.5" style={{ gridTemplateColumns: "1fr 1fr 1fr 1fr" }}>
                    <div className="text-sm font-semibold" style={{ color: "var(--ph-brand-accent)" }}>{c.month}</div>
                    <div className="text-sm tabular-nums text-right" style={{ color: "var(--ph-success)" }}>EGP {c.inflow}K</div>
                    <div className="text-sm tabular-nums text-right" style={{ color: "var(--ph-danger)" }}>EGP {c.outflow}K</div>
                    <div className="text-sm tabular-nums text-right font-semibold" style={{ color: (c.inflow - c.outflow) >= 0 ? "var(--ph-success)" : "var(--ph-danger)" }}>{c.inflow - c.outflow >= 0 ? "+" : ""}EGP {c.inflow - c.outflow}K</div>
                  </div>
                ))}
                <div className="mt-2"><DeltaChip delta={((forecastBalance - currentBalance) / Math.max(1, currentBalance)) * 100} trend={forecastBalance >= currentBalance ? "up" : "down"} /></div>
              </div>
            )}

            {wsTab === "audit" && (
              <div data-testid="fn-cash-ws-audit">
                <AuditTrail collection="cash_flow" recordId={monthData.m.month} testId="fn-cash-audit" />
              </div>
            )}
          </div>
        )}
      </Drawer>
    </div>
  );
}
